import { Location } from '@angular/common';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GuardsCheckEnd,
  Navigation,
  NavigationCancel,
  Router,
} from '@angular/router';
import { WA_SESSION_STORAGE, WA_WINDOW } from '@ng-web-apis/common';
import { readGuarded, writeGuarded } from '../guarded-storage';
import { NewVersionService } from './new-version.service';

/** Where an automatic reload goes, and whether it replaces the history entry. */
export interface ReloadTarget {
  url: string;
  replace: boolean;
}

/**
 * Back and Forward already show the target in the address bar, so loading it
 * must not add another history entry.
 */
export function navigationTarget(
  navigation: Navigation,
  url: string
): ReloadTarget {
  return {
    url,
    replace:
      !!navigation.extras.replaceUrl || navigation.trigger !== 'imperative',
  };
}

interface NavigationRecord {
  id: number;
  target: ReloadTarget;
}

const RELOADED_FOR_KEY = 'new_version_reloaded_for';
const LOCK_MS = 10_000;

/**
 * The one way this app reloads itself into a new frontend build.
 *
 * An automatic reload happens at most once per server build and tab: the
 * build is written to session storage first, and without storage that keeps
 * the write there is no automatic reload at all, since a tab stuck on an old
 * copy would otherwise reload endlessly.
 *
 * While one is under way the service is locked, and pages skip their
 * unsaved-changes prompt: the user has already left the page through the
 * app's own navigation and its leave guards. The lock lapses after a few
 * seconds, so a load the user stopped leaves the pages protected again.
 */
@Injectable({ providedIn: 'root' })
export class NewVersionReloadService {
  private readonly versions = inject(NewVersionService);
  private readonly session = inject(WA_SESSION_STORAGE);
  private readonly window = inject(WA_WINDOW);
  private readonly location = inject(Location);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);

  private readonly lock = signal(false);
  readonly locked = this.lock.asReadonly();
  private startedAutomaticLoad = false;
  private passed: NavigationRecord | null = null;
  private held: NavigationRecord | null = null;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof GuardsCheckEnd && event.shouldActivate) {
        const navigation = this.router.getCurrentNavigation();
        this.passed = navigation && {
          id: event.id,
          target: navigationTarget(navigation, event.urlAfterRedirects),
        };
      } else if (
        event instanceof NavigationCancel &&
        event.id === this.held?.id
      ) {
        const { target } = this.held;
        this.held = null;
        this.load(target);
      }
    });

    // A page restored from the back/forward cache runs the build it was left
    // on. The one that left through an automatic reload reloads at once.
    this.window.addEventListener('pageshow', (event) => {
      if (!(event as PageTransitionEvent).persisted) {
        return;
      }
      if (this.startedAutomaticLoad) {
        this.window.location.reload();
      } else {
        this.reloadIfOldBuildReloadedPast();
      }
    });
  }

  unloadPromptSuppressed(): boolean {
    return this.lock();
  }

  /** The notice's button: a plain reload that leaves every prompt in place. */
  reloadNow(): void {
    if (!this.lock()) {
      this.window.location.reload();
    }
  }

  /** Answers whether the automatic reload goes ahead, and starts it if so. */
  requestAutomaticReload(target: ReloadTarget): boolean {
    if (!this.claim()) {
      return false;
    }
    this.load(target);
    return true;
  }

  /**
   * Back and Forward may also take a page from the HTTP cache without
   * revalidating it, even though index.html is `no-cache`, so the tab comes
   * back on the build it was first loaded with.
   */
  reloadIfHistoryRestoredOldBuild(): void {
    const [entry] = this.window.performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    if (entry?.type === 'back_forward') {
      this.reloadIfOldBuildReloadedPast();
    }
  }

  /**
   * The guard's path: claims the reload and holds it until the router has
   * cancelled the navigation. By then the router has restored the history
   * entry; a full load started inside the guard would be followed by that
   * `history.replaceState`, and browsers do not agree on what that does to a
   * pending cross-document navigation.
   */
  reloadInsteadOf(navigationId: number, target: ReloadTarget): boolean {
    if (!this.claim()) {
      return false;
    }
    this.held = { id: navigationId, target };
    return true;
  }

  /** The target of the given navigation, if it got past every guard. */
  targetOfPassedNavigation(navigationId: number): ReloadTarget | null {
    return this.passed?.id === navigationId ? this.passed.target : null;
  }

  private claim(): boolean {
    const build = this.serverBuild();
    if (build === null || this.lock()) {
      return false;
    }
    if (this.reloadedFor() === build) {
      return false;
    }
    writeGuarded(this.session, RELOADED_FOR_KEY, build);
    if (this.reloadedFor() !== build) {
      return false;
    }
    this.lock.set(true);
    this.zone.runOutsideAngular(() =>
      setTimeout(() => this.zone.run(() => this.lock.set(false)), LOCK_MS)
    );
    return true;
  }

  /**
   * A page that history brought back on an old build stays there if this tab
   * already reloaded automatically for the server's build: the once-per-build
   * rule blocks the next one, and a page whose code the deploy removed could
   * no longer recover. A plain reload revalidates the page and is no history
   * navigation, so it cannot repeat. A tab that has not reloaded for that
   * build is left to the notice and its next page change.
   */
  private reloadIfOldBuildReloadedPast(): void {
    void this.versions.checkNow().then((result) => {
      const build = this.serverBuild();
      if (
        result === 'different' &&
        build !== null &&
        this.reloadedFor() === build
      ) {
        this.window.location.reload();
      }
    });
  }

  /** The server build, as it is stored once this tab reloaded for it. */
  private serverBuild(): string | null {
    return this.versions.latestServerMarker()?.join(' ') ?? null;
  }

  private reloadedFor(): string | null {
    return readGuarded(this.session, RELOADED_FOR_KEY);
  }

  private load({ url, replace }: ReloadTarget): void {
    this.startedAutomaticLoad = true;
    const address = this.location.prepareExternalUrl(url);
    if (replace) {
      this.window.location.replace(address);
    } else {
      this.window.location.assign(address);
    }
  }
}
